import { useEnum } from "@/api/services/global/enum";
import { TGetRenewVisaDocumentCreationGroupOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-document-creation-group-list.dto";
import { IconAlert, IconCheck } from "@/assets/icons";
import { Avatar } from "@/components/elements/Avatar";
import { Button } from "@/components/elements/Button";
import { Tooltip } from "@/components/elements/Tooltip";
import { DATE_TIME_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { UploadAvatarFileBtn } from "@/features/renew-visa/components/applicationStep/documentCreation/UploadAvatarFileBtn";
import { AppRoutes } from "@/routes/config";
import { background, divider, state, text } from "@/theme/colors";
import { RENEW_VISA_DOCUMENT_STEP_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { openInNewTab } from "@/utils/export";
import { findLabelByValue } from "@/utils/object";
import { getFullName, truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { Box, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { Link, useParams } from "react-router-dom";

type UseMakeDocumentCreationColumnsReturn = MRT_ColumnDef<TGetRenewVisaDocumentCreationGroupOutputDto>[];

export const useMakeDocumentCreationColumns = (readonly: boolean, isSkipStep: boolean): UseMakeDocumentCreationColumnsReturn => {
  const { data: genderOptions } = useEnum("EGender");

  const { employeeRenewVisaDocumentGroupId } = useParams();

  const columns: MRT_ColumnDef<TGetRenewVisaDocumentCreationGroupOutputDto>[] = [
    {
      id: "fullName",
      header: "外国人名",
      accessorKey: "fullName",
      Cell: ({ row }) => {
        const { employee } = row.original;
        const fullName = getFullName({ firstName: employee.firstName, lastName: employee.lastName, middleName: employee.middleName });

        return (
          <Stack direction="row" gap="6px" alignItems="center">
            <Avatar filePath={employee.thumbnailForListAvatarFilePath} width={30} height={40} sx={{ minWidth: 30 }} />
            <Typography variant="body14">{fullName ? truncateWithEllipsis(fullName, 26) : EMPTY_STR.TEXT}</Typography>
          </Stack>
        );
      },
      muiTableBodyCellProps: {
        sx: { minWidth: "unset" },
      },
      muiTableHeadCellProps: {
        sx: { minWidth: "unset", backgroundColor: divider.low },
      },
    },
    {
      id: "nationalityName",
      header: "国籍",
      accessorKey: "nationalityName",
      Cell: ({ row }) => row.original.employee?.nationality?.nationalityName || EMPTY_STR.TEXT,
      muiTableBodyCellProps: {
        sx: { minWidth: "unset" },
      },
      muiTableHeadCellProps: {
        sx: { minWidth: "unset", backgroundColor: divider.low },
      },
    },
    {
      id: "gender",
      header: "性別",
      accessorKey: "gender",
      Cell: ({ row }) => findLabelByValue(genderOptions, row.original.employee.gender) || EMPTY_STR.TEXT,
      muiTableBodyCellProps: {
        sx: { minWidth: "unset", maxWidth: "200px" },
      },
      muiTableHeadCellProps: {
        sx: { minWidth: "unset", backgroundColor: divider.low, maxWidth: "200px" },
      },
    },
    {
      id: "latestUpdatedAt",
      header: "最終更新日",
      accessorKey: "latestUpdatedAt",
      Cell: ({ row }) => (row.original.latestUpdatedAt ? dayjs(row.original.latestUpdatedAt).format(DATE_TIME_FORMAT) : EMPTY_STR.DATE),
      muiTableBodyCellProps: {
        sx: { minWidth: "unset", maxWidth: "200px" },
      },
      muiTableHeadCellProps: {
        sx: { minWidth: "unset", backgroundColor: divider.low, maxWidth: "200px" },
      },
    },
    {
      id: "latestUpdaterFullName",
      header: "更新者",
      accessorKey: "latestUpdaterFullName",
      Cell: ({ row }) => row.original.latestUpdaterFullName || EMPTY_STR.TEXT,
      muiTableBodyCellProps: {
        sx: { minWidth: "unset", maxWidth: "200px" },
      },
      muiTableHeadCellProps: {
        sx: { minWidth: "unset", backgroundColor: divider.low, maxWidth: "200px" },
      },
    },
    {
      id: "employee-company-application",
      header: "編集",
      Cell: ({ row }) => {
        const status =
          !row.original.renewVisaDocumentStep.renewVisaEmployeeApplicationDocument?.status &&
          !row.original.renewVisaDocumentStep.renewVisaCompanyApplicationDocument?.status
            ? undefined
            : [
                  row.original.renewVisaDocumentStep.renewVisaEmployeeApplicationDocument?.status ?? RENEW_VISA_DOCUMENT_STEP_STATUS.IN_PROGRESS,
                  row.original.renewVisaDocumentStep.renewVisaCompanyApplicationDocument?.status ?? RENEW_VISA_DOCUMENT_STEP_STATUS.IN_PROGRESS,
                ].includes(RENEW_VISA_DOCUMENT_STEP_STATUS.IN_PROGRESS)
              ? RENEW_VISA_DOCUMENT_STEP_STATUS.IN_PROGRESS
              : RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE;

        return (
          <Button
            disabled={isSkipStep}
            variant="outline"
            size="sm"
            component={Link}
            to={
              readonly
                ? getDynamicRoute(AppRoutes.renewVisaInfoHistoryEmployeeApplication, {
                    employeeRenewVisaDocumentGroupId,
                    renewVisaDocumentId: row.original.renewVisaDocumentId,
                    documentStepId: row.original.renewVisaDocumentStep.id,
                  })
                : getDynamicRoute(AppRoutes.renewVisaPresentEmployeeApplication, {
                    employeeRenewVisaDocumentGroupId,
                    renewVisaDocumentId: row.original.renewVisaDocumentId,
                    documentStepId: row.original.renewVisaDocumentStep.id,
                  })
            }
          >
            <Stack
              mr={1}
              alignItems="center"
              justifyContent="center"
              width={16}
              height={16}
              borderRadius={16}
              bgcolor={status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE ? state.success_1 : divider.high}
            >
              <IconCheck color={background.white} />
            </Stack>
            編集
          </Button>
        );
      },
      muiTableHeadCellProps: {
        sx: { backgroundColor: divider.low, borderLeft: "1px solid", maxWidth: "107px !important", minWidth: "107px !important" },
      },
      muiTableBodyCellProps: {
        sx: { borderLeft: "1px solid", maxWidth: "107px !important", minWidth: "107px !important" },
      },
    },
    {
      id: "upload-application",
      header: "添付書類",
      Cell: ({ row }) => {
        const status = row.original.renewVisaDocumentStep.renewVisaUploadFile?.status;
        return (
          <Button
            disabled={isSkipStep}
            variant="outline"
            size="sm"
            component={Link}
            to={
              readonly
                ? getDynamicRoute(AppRoutes.renewVisaInfoHistoryUploadApplication, {
                    employeeRenewVisaDocumentGroupId,
                    renewVisaDocumentId: row.original.renewVisaDocumentId,
                    documentStepId: row.original.renewVisaDocumentStep.id,
                  })
                : getDynamicRoute(AppRoutes.renewVisaPresentUploadApplication, {
                    employeeRenewVisaDocumentGroupId,
                    renewVisaDocumentId: row.original.renewVisaDocumentId,
                    documentStepId: row.original.renewVisaDocumentStep.id,
                  })
            }
          >
            <Stack
              mr={1}
              alignItems="center"
              justifyContent="center"
              width={16}
              height={16}
              borderRadius={16}
              bgcolor={status === RENEW_VISA_DOCUMENT_STEP_STATUS.COMPLETE ? state.success_1 : divider.high}
            >
              <IconCheck color={background.white} />
            </Stack>
            アップロード
          </Button>
        );
      },
      muiTableHeadCellProps: {
        sx: { backgroundColor: divider.low, borderLeft: "1px solid" },
      },
      muiTableBodyCellProps: {
        sx: { borderLeft: "1px solid" },
      },
    },
    {
      id: "upload-avatar-file",
      header: "顔写真",
      Header: () => (
        <Stack flexDirection="row" alignItems="center" gap={1}>
          <Typography component="p" padding="0 !important" variant="sub16Bold">
            顔写真
          </Typography>
          <Box mt={0.5}>
            <Tooltip
              width={420}
              content={
                <Typography component="p" variant="body14" color={text.secondary}>
                  6ヶ月以内の正面を向いた写真を用意してください。
                  <br /> 顔写真の詳細については、
                  <Typography
                    variant="body14"
                    onClick={() => openInNewTab("https://www.moj.go.jp/isa/applications/status/photo_info_00002.html")}
                    color={state.link_1}
                    sx={{ cursor: "pointer", textDecoration: "underline" }}
                  >
                    出入国在留管理庁「提出写真の規格」
                  </Typography>
                  をご確認ください。
                </Typography>
              }
            >
              <IconAlert width={20} height={20} color={text.tertiary} />
            </Tooltip>
          </Box>
        </Stack>
      ),
      Cell: ({ row }) => {
        const status = row.original.renewVisaDocumentStep.renewVisaUploadAvatarFile?.status;
        return (
          <UploadAvatarFileBtn
            documentStepId={row.original.renewVisaDocumentStep.id.toString()}
            renewVisaDocumentId={row.original.renewVisaDocumentId.toString()}
            status={status}
            readonly={readonly || isSkipStep}
          />
        );
      },
      muiTableHeadCellProps: {
        sx: { backgroundColor: divider.low, borderLeft: "1px solid" },
      },
      muiTableBodyCellProps: {
        sx: { borderLeft: "1px solid" },
      },
    },
  ];

  return columns;
};
